import { Helmet } from "react-helmet-async";
import useSWR from "swr";
import { AxiosError } from "axios";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import { useScrollFadeIn } from "../../utils/hooks";
import type { ProductsResponse } from "../../utils/api";
import { getSummary, getProducts } from "../../utils/api";
import { MailButton } from "../../components/Button";
import ScrollToTopButton from "../../components/ScrollToTop/ScrollToTopButton";
import userGuide from "../../assets/pdf/user-guide.pdf";
import { normalizeImagePath } from "../../utils/helpers";

// 共通CSSは常に読み込み
import "@scss/pr_tc.scss";
import Footer from "../../components/Footer";

export default function TableConroPage() {
    const { data: productsDataStove } = useSWR(
        "productsDataStove",
        () => getProducts("stove"),
        {
            revalidateOnFocus: false, // ページフォーカス時の再フェッチを無効化
            onError: (err: AxiosError) => {
                console.error("Error fetching products stove data:", err);
                // ここでエラー表示などを行いたい場合は追加
            },
        }
    );
    const { data: summaryDataStove } = useSWR(
        "summaryDataStove",
        () => getSummary("stove"),
        {
            revalidateOnFocus: false, // ページフォーカス時の再フェッチを無効化
            onError: (err: AxiosError) => {
                console.error("Error fetching summary stove data:", err);
                // ここでエラー表示などを行いたい場合は追加
            },
        }
    );
    const [activeProducts, setActiveProducts] = useState<ProductsResponse[]>(
        []
    );
    const [recommendedProducts, setRecommendedProducts] = useState<
        ProductsResponse[]
    >([]);
    const [isActive, setIsActive] = useState(false);

    const handleRecommendedClick = (productId: string) => {
        if (activeProducts.find((product) => product.id === productId)) {
            const html = document.documentElement;
            const prevBehavior = html.style.scrollBehavior;
            html.style.scrollBehavior = "smooth";

            window.location.href = `/pr_tableconro#${productId}`;

            setTimeout(() => {
                html.style.scrollBehavior = prevBehavior;
            }, 800);
        }
    };
    // 各要素の toggle 状態（true/false）を管理
    const [dataListActive, setDataListActive] = useState<boolean[]>(
        new Array(50).fill(false)
    );
    const [accActive, setAccActive] = useState([false, true, true]);
    const [faqActive, setFaqActive] = useState<boolean[]>(
        new Array(20).fill(false)
    );

    useEffect(() => {
        if (productsDataStove) {
            const actProducts = productsDataStove.filter(
                (product) => product.is_active
            );
            setRecommendedProducts(
                actProducts
                    .filter((product) => product.is_recommended)
                    .sort(
                        (a, b) =>
                            (a?.recommended_sort_order ?? 0) -
                            (b?.recommended_sort_order ?? 0)
                    )
            );
            setActiveProducts(actProducts);
        }
    }, [productsDataStove]);

    useScrollFadeIn("fade-in", [activeProducts]);
    useScrollFadeIn("fade-in-up", [activeProducts]);
    const faqList = [
        {
            q: "誰でも購入できますか？",
            a: (
                <>
                    <p>
                        西部ガス・西部ガス熊本・西部ガス佐世保・西部ガス長崎のサービスエリア内※で都市ガスをご利用であれば、上記4社とガス・電気契約が無いお客さまもご購入いただけます。
                    </p>
                    <ul className="attListMini">
                        <li>
                            福岡・熊本・長崎の3県15市15町。詳しくは
                            <a
                                href="https://hd.saibugas.co.jp/business/gas/?&amp;_gl=1*admsyw*_ga*Mzc4MTA2MjgzLjE3Mzk3NjUxMzQ.*_ga_HYEYQESJTW*czE3NTQ0Njk1OTQkbzE2JGcwJHQxNzU0NDY5NTk0JGo2MCRsMCRoMA..&amp;_ga=2.124442953.237105303.1754444476-378106283.1739765134#serviceArea"
                                target="_blank"
                                className="blueLink"
                            >
                                こちら
                            </a>
                            をご確認ください。
                        </li>
                    </ul>
                </>
            ),
        },
        {
            q: "購入できるコンロの種類はこのサイト上の機種のみですか？",
            a: (
                <>
                    <p>
                        はい、本サイトに掲載している機種のみのお取扱いとなります。
                        <br />
                        その他の機種につきましては、最寄りの西部ガスリビングメイト店舗に直接お問い合わせください。
                    </p>
                </>
            ),
        },
        {
            q: "電話やFAXでも注文できますか？",
            a: (
                <>
                    <p>
                        西部ガスグループ公式オンラインショップでは、インターネットからのご注文のみ承っております。
                        <br />
                        お電話によるご注文は承っておりません。
                        <br />
                        ご了承ください。
                    </p>
                </>
            ),
        },
        {
            q: "注文履歴は確認できますか？",
            a: (
                <>
                    <p>
                        申し訳ございません。当サイトから確認することはできません。商品購入時にお送りしたSMSでご確認ください。
                    </p>
                </>
            ),
        },
        {
            q: "支払い方法は選べますか？",
            a: (
                <>
                    <p>
                        お支払い方法は
                        <a href="#payment" className="blueLink">
                            こちら
                        </a>
                        からご確認ください。
                    </p>
                </>
            ),
        },
        {
            q: "注文した商品の配送対応エリアを教えてください。",
            a: (
                <>
                    <p>
                        西部ガス・西部ガス熊本・西部ガス佐世保・西部ガス長崎のサービスエリア内※が配送エリアとなります。
                    </p>
                    <ul className="attListMini">
                        <li>
                            福岡・熊本・長崎の3県15市15町。詳しくは
                            <a
                                href="https://hd.saibugas.co.jp/business/gas/#serviceArea"
                                target="_blank"
                                className="blueLink"
                            >
                                こちら
                            </a>
                            をご確認ください。
                        </li>
                    </ul>
                </>
            ),
        },
        {
            q: "配送業者は指定できますか？",
            a: (
                <>
                    <p>
                        配送業者の指定はできません。弊社指定の販売店（もよりの西部ガス販売店（リビングメイト））より配送させて頂きます。
                    </p>
                </>
            ),
        },
        {
            q: "古い機器の回収はできますか？",
            a: (
                <>
                    <p>
                        ご注文をいただいた商品を取替後に不要となったガス機器は回収させていただきます。
                        <br />
                        ご注文いただいた商品以外の古い機器の回収は承っておりません。「各地方自治体の粗大ごみ扱い」にてご確認ください。
                    </p>
                </>
            ),
        },
        {
            q: "見積書や請求書の発行はできますか？",
            a: (
                <>
                    <p>
                        申し訳ありません。当ショップでは、見積書・請求書の発行は行っておりません。ご了承ください。
                    </p>
                </>
            ),
        },
        {
            q: "領収書の発行はできますか？",
            a: (
                <>
                    <p>
                        お客さまからご要望をいただいた場合、領収書を発行いたします。お手数かけしますが、領収書が必要な場合は
                        <a href="#contact" className="blueLink">
                            こちら
                        </a>
                        の問い合わせフォームより連絡お願いいたします。
                    </p>
                </>
            ),
        },
    ];
    return (
        <>
            <Helmet>
                <title>
                    【2万円台から買える西部ガスのテーブルコンロ】配送・設置・ホース・古い機器の処分がすべて無料！
                </title>
                <link
                    rel="canonical"
                    href="https://onlineshop.saibugas.co.jp/pr_tableconro/"
                />
                <meta
                    name="description"
                    content="2万円台から買える西部ガスのテーブルコンロ。配送・設置・ホース・古い機器の処分がすべて無料！さらに通常価格より全商品50%OFF！お買い替えは今がチャンスです。"
                />

                <meta
                    property="og:title"
                    content="【2万円台から買える西部ガスのテーブルコンロ】配送・設置・ホース・古い機器の処分がすべて無料！"
                />
                <meta
                    property="og:description"
                    content="2万円台から買える西部ガスのテーブルコンロ。配送・設置・ホース・古い機器の処分がすべて無料！さらに通常価格より全商品50%OFF！お買い替えは今がチャンスです。"
                />
                <meta
                    property="og:url"
                    content="https://onlineshop.saibugas.co.jp/pr_tableconro/"
                />
                <meta
                    property="og:site_name"
                    content="【2万円台から買える西部ガスのテーブルコンロ】配送・設置・ホース・古い機器の処分がすべて無料！"
                />
                <meta property="og:image" content="/assets/img/pr_tc/ogp.png" />
                <meta name="robots" content="index,follow" />

                <style>{`html { font-size: 62.5% !important; }`}</style>
            </Helmet>
            <div className="wrap page-pr_tc" id="pageTop">
                <ScrollToTopButton />
                <div className="sideNav fade-in-up">
                    <nav>
                        <div className="navBox">
                            <h2>MENU</h2>
                            <ul className="">
                                <li>
                                    <a href="#which" id="">
                                        コンロの見分け方をチェック
                                    </a>
                                </li>
                                <li>
                                    <a href="#point" id="">
                                        5つの安心ポイント
                                    </a>
                                </li>
                                <li>
                                    <a href="#push" id="">
                                        おすすめテーブルコンロ
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href="#product"
                                        id="tcpr2024_navipc_product"
                                    >
                                        テーブルコンロ一覧
                                    </a>
                                </li>
                                <li>
                                    <a href="#flow" id="">
                                        ご利用の流れ
                                    </a>
                                </li>
                                <li>
                                    <a href="#area" id="">
                                        配送対応エリア
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href="#payment"
                                        id="tcpr2024_navipc_payment"
                                    >
                                        お支払い方法
                                    </a>
                                </li>
                                <li>
                                    <a href="#faq" id="">
                                        よくあるご質問
                                    </a>
                                </li>
                                <li>
                                    <a href="#contact" id="">
                                        お問い合わせ
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </nav>
                </div>
                <div className="sideQr fade-in-up">
                    <p>
                        <img
                            src="/assets/img/pr_tc/qr.png"
                            alt="スマホからのアクセスはこちら"
                            loading="eager"
                        />
                    </p>
                </div>
                <main className={isActive ? "open" : ""}>
                    <div
                        className="spMenuSet"
                        onClick={() => setIsActive(!isActive)}
                    >
                        {" "}
                        <div id="nav-toggle">
                            <div>
                                {" "}
                                <span></span> <span></span> <span></span>{" "}
                            </div>
                        </div>{" "}
                    </div>
                    <header>
                        <h1 className="logo-wrap">
                            <a href="/">
                                <span className="logo">
                                    <img
                                        src="/assets/img/pr_tc/logo.png"
                                        alt="西部ガスグループ"
                                    />
                                </span>
                                <span className="logo-sub">公式オンラインショップ</span>
                            </a>
                        </h1>
                    </header>
                    <nav id="global-nav">
                        <div className="gnInr">
                            <ul className="gnBox smoothLinkSP">
                                <li onClick={() => setIsActive(false)}>
                                    <a href="#which" id="tcpr2024_menu_which">
                                        コンロの見分け方をチェック
                                    </a>
                                </li>
                                <li onClick={() => setIsActive(false)}>
                                    <a href="#point" id="tcpr2024_menu_point">
                                        5つの安心ポイント
                                    </a>
                                </li>
                                <li onClick={() => setIsActive(false)}>
                                    <a href="#push" id="tcpr2024_menu_push">
                                        おすすめテーブルコンロ
                                    </a>
                                </li>
                                <li onClick={() => setIsActive(false)}>
                                    <a
                                        href="#product"
                                        id="tcpr2024_menu_product"
                                    >
                                        テーブルコンロ一覧
                                    </a>
                                </li>
                                <li onClick={() => setIsActive(false)}>
                                    <a href="#flow" id="tcpr2024_menu_flow">
                                        ご利用の流れ
                                    </a>
                                </li>
                                <li onClick={() => setIsActive(false)}>
                                    <a href="#area" id="tcpr2024_menu_area">
                                        配送対応エリア
                                    </a>
                                </li>
                                <li onClick={() => setIsActive(false)}>
                                    <a
                                        href="#payment"
                                        id="tcpr2024_menu_payment"
                                    >
                                        お支払い方法
                                    </a>
                                </li>
                                <li onClick={() => setIsActive(false)}>
                                    <a href="#faq" id="tcpr2024_menu_faq">
                                        よくあるご質問
                                    </a>
                                </li>
                                <li onClick={() => setIsActive(false)}>
                                    <a
                                        href="#contact"
                                        id="tcpr2024_menu_contact"
                                    >
                                        お問い合わせ
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </nav>
                    <div className="contents">
                        <section className="mv">
                            <h2 style={{ padding: "2rem" }}>
                                <img
                                    src="/assets/img/pr_tc/mv_01.png"
                                    alt="2万円台から買える西部ガスのテーブルコンロ"
                                    loading="eager"
                                />
                            </h2>
                            <p className="attP">
                                ※都市ガス(12A・13A)用商品です
                            </p>
                            <p className="product">
                                <img
                                    src="/assets/img/pr_tc/mv_02.png"
                                    alt="テーブルコンロ商品写真"
                                    loading="eager"
                                    style={{ margin: "0 auto" }}
                                />
                            </p>
                            <p className="allFree">
                                <img
                                    src="/assets/img/pr_tc/mv_03.png"
                                    alt="配送・設置・ホース・処分 すべて無料！"
                                    loading="eager"
                                    style={{
                                        display: "inline",
                                    }}
                                />
                            </p>
                        </section>
                        <div className="mv_btn smoothLink">
                            <a
                                href="#product"
                                className="orange_btn arrow noto-sans-jp-black"
                                id="tcpr2024_mv_cv_product"
                            >
                                {summaryDataStove?.max_discount_rate &&
                                summaryDataStove?.max_discount_rate > 0 ? (
                                    <span className="fukidashi">
                                        通常価格より最大
                                        <span className="num">
                                            {
                                                summaryDataStove?.max_discount_rate
                                            }
                                        </span>
                                        %OFF
                                    </span>
                                ) : (
                                    <>
                                        {summaryDataStove?.has_outlet_products && (
                                            <img
                                                className="outlet_icon"
                                                src="/assets/img/common/outlet.png"
                                                alt="アウトレット"
                                            />
                                        )}
                                    </>
                                )}
                                テーブルコンロ一覧
                            </a>
                        </div>
                        {/* <section className="newsSec fade-in">
                            <div className="news_wrap">
                                <ul className="newsList">
                                    <li>
                                        <a href="#" className="news_link">
                                            <span className="news_date">
                                                2025/07/31
                                            </span>
                                            <p className="news_title">
                                                重要なお知らせが入ります。重要なお知らせが入ります。
                                            </p>
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </section> */}
                        <section
                            className="whichSec action fade-in-up"
                            id="which"
                            style={{
                                marginTop: "6rem",
                            }}
                        >
                            <dl
                                className="accDl"
                                onClick={() =>
                                    setAccActive([
                                        !accActive[0],
                                        accActive[1],
                                        accActive[2],
                                    ])
                                }
                            >
                                <dt
                                    id="tcpr2024_which"
                                    className={`${accActive[0] ? "open" : ""}`}
                                >
                                    <h2>
                                        {" "}
                                        <span>今使ってるコンロはどっち？</span>
                                        <br />
                                        コンロの見分け方をチェック！{" "}
                                    </h2>

                                    <div className="flex">
                                        <p className="txt">
                                            家庭用のコンロは&#9312;テーブルコンロと&#9313;ビルトインコンロの2種類に分かれます。どちらを取り付けるか、ご自宅のキッチンの種類で選びましょう。
                                        </p>
                                        <p className="img">
                                            <img
                                                src="/assets/img/pr_tc/which_01.png"
                                                alt="チェック！"
                                                loading="eager"
                                            />
                                        </p>
                                    </div>
                                    <p className="arrow"></p>
                                </dt>
                                <motion.div
                                    className="flow1 fade-in-up"
                                    initial={false}
                                    animate={{
                                        height: accActive[0] ? "auto" : 0,
                                    }}
                                    transition={{
                                        duration: 0.35,
                                        ease: [0.25, 0.46, 0.45, 0.94],
                                    }}
                                    style={{
                                        overflow: "hidden",
                                    }}
                                >
                                    <motion.dd
                                        initial={false}
                                        animate={{
                                            opacity: accActive[0] ? 1 : 0,
                                            y: accActive[0] ? 0 : -10,
                                        }}
                                        transition={{
                                            duration: 0.25,
                                            delay: accActive[0] ? 0.1 : 0,
                                            ease: "easeOut",
                                        }}
                                    >
                                        <div className="pattern1 fade-in-up">
                                            <div className="inr">
                                                <h3>
                                                    コンロを置くスペースがあるキッチンの場合
                                                </h3>
                                                <p className="txt1">
                                                    片側に
                                                    <span className="bold">
                                                        コンロを置くスペースがあるキッチンは
                                                    </span>
                                                    <br />
                                                    <span className="boldBig">
                                                        テーブルコンロ
                                                    </span>
                                                    を設置できます。
                                                </p>
                                            </div>
                                            <p className="img">
                                                <img
                                                    src="/assets/img/pr_tc/which_02.png"
                                                    alt="テーブルコンロ"
                                                    loading="lazy"
                                                />
                                            </p>
                                            <p className="canBuy">
                                                <img
                                                    src="/assets/img/pr_tc/which_03.png"
                                                    alt=""
                                                    loading="lazy"
                                                    style={{
                                                        display: "inline",
                                                    }}
                                                />
                                                本サイトでお買い求めいただけます
                                            </p>
                                        </div>
                                        <div className="pattern2 fade-in-up">
                                            <div className="inr">
                                                <h3>システムキッチンの場合</h3>
                                                <p className="txt1">
                                                    {" "}
                                                    ご自宅のキッチンが
                                                    <span className="bold">
                                                        システムキッチンの場合は
                                                    </span>
                                                    <br />
                                                    <span className="boldBig">
                                                        ビルトインコンロ
                                                    </span>
                                                    になります。キッチンに組み込むよう設計されているため工事が必要です。
                                                </p>
                                            </div>
                                            <p className="img">
                                                <img
                                                    src="/assets/img/pr_tc/which_04.png"
                                                    alt="ビルトインコンロ"
                                                    loading="lazy"
                                                />
                                            </p>
                                            <p className="cantBuy">
                                                本サイトではお取扱いしておりません。
                                                <br />
                                                <a
                                                    href="https://www.saibugas.co.jp/campaign/reformcp2024/index.html?utm_source=hpsg&utm_medium=hp&utm_campaign=24ec-tableconro_conro-type"
                                                    target="_blank"
                                                    className="blueLink"
                                                >
                                                    こちら
                                                </a>
                                                でご相談ください。
                                            </p>
                                        </div>
                                    </motion.dd>
                                </motion.div>
                            </dl>
                        </section>
                        <section className="pointSec" id="point">
                            <h2 className="fade-in-up">
                                {" "}
                                <span>西部ガスでコンロを購入する</span>{" "}
                                5つの安心ポイント{" "}
                            </h2>
                            <ol className="pointList">
                                <li className="fade-in-up">
                                    <p className="img">
                                        <span className="num">1</span>
                                        <img
                                            src="/assets/img/pr_tc/point_01.png"
                                            loading="lazy"
                                            alt=""
                                        />
                                    </p>
                                    <div className="data">
                                        <h3>
                                            {" "}
                                            <span className="marker">
                                                配達が無料
                                            </span>{" "}
                                            <span className="free">
                                                <img
                                                    src="/assets/img/pr_tc/point_free.png"
                                                    loading="lazy"
                                                    alt=""
                                                />
                                            </span>{" "}
                                        </h3>
                                        <p className="txt">
                                            ご購入いただいたコンロは、ご自宅まで無料でお届けします！重くて大きなコンロを運び入れる手間もなく、お得＆ラクチン&#9834;
                                        </p>
                                    </div>
                                </li>
                                <li className="fade-in-up">
                                    <p className="img">
                                        <span className="num">2</span>
                                        <img
                                            src="/assets/img/pr_tc/point_02.png"
                                            loading="lazy"
                                            alt=""
                                        />
                                    </p>
                                    <div className="data">
                                        <h3>
                                            {" "}
                                            <span className="marker">
                                                設置も無料
                                            </span>{" "}
                                            <span className="free">
                                                <img
                                                    src="/assets/img/pr_tc/point_free.png"
                                                    loading="lazy"
                                                    alt=""
                                                />
                                            </span>{" "}
                                        </h3>
                                        <p className="txt">
                                            テーブルコンロはご自身でホースの取り替えや設置をしないといけないケースが多いですが、西部ガスなら設置も無料で対応！プロに任せて安心！
                                        </p>
                                    </div>
                                </li>
                                <li className="fade-in-up">
                                    <p className="img">
                                        <span className="num">3</span>
                                        <img
                                            src="/assets/img/pr_tc/point03_2.png"
                                            loading="lazy"
                                            alt=""
                                        />
                                    </p>
                                    <div className="data">
                                        <h3>
                                            {" "}
                                            <span className="marker">
                                                ホースも無料
                                            </span>{" "}
                                            <span className="free">
                                                <img
                                                    src="/assets/img/pr_tc/point_free.png"
                                                    loading="lazy"
                                                    alt=""
                                                />
                                            </span>{" "}
                                        </h3>
                                        <p className="txt">
                                            コンロのお取り替えで忘れがちなガスホース。西部ガスなら、設置の際に無料で新品のガスホースにお取り替えいたします！
                                        </p>
                                        <p className="txt">
                                            ※ガスホースは、ガス栓の接続部形状によっては有料の部材が追加で必要になる場合があります。
                                        </p>
                                    </div>
                                </li>
                                <li className="fade-in-up">
                                    <p className="img">
                                        <span className="num">4</span>
                                        <img
                                            src="/assets/img/pr_tc/point_04.png"
                                            loading="lazy"
                                            alt=""
                                        />
                                    </p>
                                    <div className="data">
                                        <h3>
                                            {" "}
                                            <span className="marker">
                                                処分も無料
                                            </span>{" "}
                                            <span className="free">
                                                <img
                                                    src="/assets/img/pr_tc/point_free.png"
                                                    loading="lazy"
                                                    alt=""
                                                />
                                            </span>{" "}
                                        </h3>
                                        <p className="txt">
                                            処分も引き取りも有料になるケースが多い古いコンロ。西部ガスなら、無料で処分をお引き受けします!処分方法を気にせずに新しいコンロにお取り替えできますよ&#9834;
                                        </p>
                                    </div>
                                </li>
                                <li className="fade-in-up">
                                    <p className="img">
                                        <span className="num">5</span>
                                        <img
                                            src="/assets/img/pr_tc/point_05.png"
                                            loading="lazy"
                                            alt=""
                                        />
                                    </p>
                                    <div className="data">
                                        <h3>
                                            {" "}
                                            <span className="marker">
                                                使用方法をしっかりご説明
                                            </span>{" "}
                                        </h3>
                                        <p className="txt">
                                            専門業者でない場合、使い方や機器の説明が不十分な場合も。西部ガスはガス機器のプロが、使用方法だけでなくお客さまの疑問点にもしっかりお答えします！
                                        </p>
                                    </div>
                                </li>
                            </ol>
                            <div
                                className="smoothLink"
                                style={{ marginTop: "6rem" }}
                            >
                                <a
                                    href="#product"
                                    className="orange_btn arrow fade-in-up noto-sans-jp-black"
                                    id="tcpr2024_mv_cv_product"
                                >
                                    {summaryDataStove?.max_discount_rate &&
                                    summaryDataStove?.max_discount_rate > 0 ? (
                                        <span className="fukidashi">
                                            通常価格より最大
                                            <span className="num">
                                                {
                                                    summaryDataStove?.max_discount_rate
                                                }
                                            </span>
                                            %OFF
                                        </span>
                                    ) : (
                                        <>
                                            {summaryDataStove?.has_outlet_products && (
                                                <img
                                                    className="outlet_icon"
                                                    src="/assets/img/common/outlet.png"
                                                    alt="アウトレット"
                                                />
                                            )}
                                        </>
                                    )}
                                    テーブルコンロ一覧
                                </a>
                            </div>
                        </section>
                        <section className="pushSec" id="push">
                            <h2 className="fade-in-up pushTitle">
                                <div>
                                    <p className="obi noto-sans-jp-semibold">
                                        料理好き！西部ガス社員 久保の
                                    </p>
                                    <p className="text noto-sans-jp-black">
                                        おすすめ
                                        <br />
                                        テーブルコンロ
                                    </p>
                                </div>
                                <div
                                    style={{ maxWidth: "12rem", maxHeight: "12rem" }}
                                >
                                    <img
                                        src="/assets/img/pr_tc/push.png"
                                        alt="料理好き！西部ガス社員 久保のおすすめテーブルコンロ3選"
                                        loading="lazy"
                                    />
                                </div>
                            </h2>
                            <ol className="pushList">
                                {recommendedProducts?.length > 0 &&
                                    recommendedProducts
                                        ?.slice(0, 3)
                                        .map((product, index) => (
                                            <li
                                                className={`li0${
                                                    index + 1
                                                } fade-in-up`}
                                                key={index}
                                            >
                                                <h3>
                                                    <span className="num">
                                                        {index + 1}
                                                    </span>
                                                    <span className="txt">
                                                        {
                                                            product.recommended_headline
                                                        }
                                                    </span>
                                                </h3>
                                                <p className="img">
                                                    <img
                                                        src={normalizeImagePath(product.eyecatch_image_url)}
                                                        alt={product.name ?? ""}
                                                        loading="lazy"
                                                    />
                                                </p>
                                                <p className="desc">
                                                    {product.recommended_body}
                                                </p>
                                                <button
                                                    className="black_btn arrow noto-sans-jp-bold"
                                                    onClick={() =>
                                                        handleRecommendedClick(
                                                            product.id
                                                        )
                                                    }
                                                >
                                                    詳細を見る
                                                </button>
                                            </li>
                                        ))}
                            </ol>
                        </section>
                        <section className="productSec" id="product">
                            <h2 className="fade-in-up">テーブルコンロ一覧</h2>
                            <ul>
                                {activeProducts?.map((product, index) => {
                                    const discount =
                                        product.standard_price &&
                                        product.standard_price > 0
                                            ? true
                                            : false;
                                    return (
                                        <li
                                            className="productBox fade-in-up"
                                            id={product.id}
                                            key={product.id}
                                        >
                                            <h3
                                                className={`main_ttl ${
                                                    product.is_outlet
                                                        ? "outlet"
                                                        : ""
                                                }`}
                                            >
                                                <p
                                                    style={{
                                                        fontSize: "1.2rem",
                                                    }}
                                                >
                                                    {product.comment}
                                                </p>
                                                {product.name}
                                            </h3>
                                            <div className="product_list-inr">
                                                <div className="productImg">
                                                    <img
                                                        src={normalizeImagePath(product.eyecatch_image_url)}
                                                        alt={`${product.name} ${product.model}`}
                                                        loading="lazy"
                                                    />
                                                </div>
                                                {discount && (
                                                    <p
                                                        className="standard_price"
                                                        style={{
                                                            marginBottom:
                                                                "2rem",
                                                        }}
                                                    >
                                                        標準価格&nbsp;
                                                        <span className="en">
                                                            &yen;
                                                            {product.standard_price?.toLocaleString()}
                                                        </span>
                                                        &nbsp;(税込)
                                                    </p>
                                                )}
                                                <div className="price">
                                                    {discount &&
                                                        product.discount_rate !=
                                                            null &&
                                                        product.discount_rate >
                                                            0 && (
                                                            <div className="discount_rate">
                                                                {
                                                                    product.discount_rate
                                                                }
                                                                %OFF
                                                            </div>
                                                        )}
                                                    <div
                                                        style={{
                                                            position:
                                                                "relative",
                                                            display: "flex",
                                                            flexDirection:
                                                                "column",
                                                            alignItems:
                                                                "flex-start",
                                                        }}
                                                    >
                                                        {product.is_limited &&
                                                            product.inventory >
                                                                0 && (
                                                                <span className="limited">
                                                                    先着
                                                                    {
                                                                        product.inventory
                                                                    }
                                                                    台限り
                                                                </span>
                                                            )}
                                                        <div
                                                            style={{
                                                                lineHeight: "1",
                                                            }}
                                                        >
                                                            {discount ? (
                                                                <span>
                                                                    特別価格
                                                                </span>
                                                            ) : (
                                                                <span>
                                                                    販売価格
                                                                </span>
                                                            )}
                                                            <span className="en">
                                                                &yen;
                                                                {product.sales_price?.toLocaleString()}
                                                            </span>
                                                            (税込)
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="btn">
                                                    {product.inventory > 0 ? (
                                                        <Link
                                                            to={`/order/${product.id}`}
                                                            className="orange_btn cart btn noto-sans-jp-black"
                                                            onClick={() => {
                                                                const html =
                                                                    document.documentElement;
                                                                html.style.scrollBehavior =
                                                                    "auto";
                                                                setTimeout(
                                                                    () => {
                                                                        html.style.scrollBehavior =
                                                                            "smooth";
                                                                    },
                                                                    800
                                                                );
                                                            }}
                                                        >
                                                            {product.is_low_stock && (
                                                                <img
                                                                    className="low_stock_icon"
                                                                    src="/assets/img/common/low_stock.png"
                                                                    alt="残りわずか"
                                                                />
                                                            )}
                                                            注文画面へ
                                                        </Link>
                                                    ) : (
                                                        <div className="orange_btn cart btn noto-sans-jp-black">
                                                            注文画面へ
                                                            <div className="out_of_stock">
                                                                完売しました
                                                            </div>
                                                        </div>
                                                        // <Link
                                                        //     to={`/order/${product.id}`}
                                                        //     className="orange_btn cart btn noto-sans-jp-black"
                                                        //     onClick={() => {
                                                        //         const html =
                                                        //             document.documentElement;
                                                        //         html.style.scrollBehavior =
                                                        //             "auto";
                                                        //         setTimeout(
                                                        //             () => {
                                                        //                 html.style.scrollBehavior =
                                                        //                     "smooth";
                                                        //             },
                                                        //             800
                                                        //         );
                                                        //     }}
                                                        // >
                                                        //     在庫0テスト：注文画面へ
                                                        // </Link>
                                                    )}
                                                </div>
                                                <p className="attMini">
                                                    ※商品の引渡し後の返品または交換は、
                                                    <br />
                                                    原則として承りませんのでご注意ください（詳細は
                                                    <a
                                                        href={userGuide}
                                                        target="_blank"
                                                    >
                                                        こちら
                                                    </a>
                                                    ）
                                                </p>
                                                <motion.div
                                                    className={`dataList ${
                                                        dataListActive[index]
                                                            ? "dataListOpen"
                                                            : "dataListClose"
                                                    }`}
                                                    initial={false}
                                                    animate={{
                                                        height: dataListActive[
                                                            index
                                                        ]
                                                            ? "auto"
                                                            : "100px",
                                                    }}
                                                    transition={{
                                                        duration: 0.2,
                                                        ease: [
                                                            0.25, 0.46, 0.45,
                                                            0.94,
                                                        ],
                                                    }}
                                                    onClick={() => {
                                                        setDataListActive(
                                                            (prev) => {
                                                                const next = [
                                                                    ...prev,
                                                                ];
                                                                next[index] =
                                                                    !next[
                                                                        index
                                                                    ];
                                                                return next;
                                                            }
                                                        );
                                                    }}
                                                >
                                                    <dl>
                                                        <dt>メーカー</dt>
                                                        <dd>
                                                            {
                                                                product.manufacturer
                                                            }
                                                        </dd>
                                                    </dl>
                                                    <dl>
                                                        <dt>型式</dt>
                                                        <dd>{product.model}</dd>
                                                    </dl>
                                                    <dl>
                                                        <dt>天板サイズ</dt>
                                                        <dd>
                                                            {
                                                                product.top_board_size
                                                            }
                                                        </dd>
                                                    </dl>
                                                    <dl>
                                                        <dt>カラー</dt>
                                                        <dd>{product.color}</dd>
                                                    </dl>
                                                    <dl>
                                                        <dt>ガス種</dt>
                                                        <dd>
                                                            都市ガス(12A・13A)
                                                        </dd>
                                                    </dl>
                                                    <dl>
                                                        <dt>トッププレート</dt>
                                                        <dd>
                                                            {product.top_plate}
                                                        </dd>
                                                    </dl>
                                                    <dl>
                                                        <dt>外形寸法(mm)</dt>
                                                        <dd>
                                                            幅
                                                            {
                                                                product.size_width_mm
                                                            }
                                                            ×奥行
                                                            {
                                                                product.size_depth_mm
                                                            }
                                                            ×高さ
                                                            {
                                                                product.size_height_mm
                                                            }
                                                        </dd>
                                                    </dl>
                                                    <dl>
                                                        <dt>
                                                            グリル庫内寸法(mm)
                                                        </dt>
                                                        <dd>
                                                            幅
                                                            {
                                                                product.grill_width_mm
                                                            }
                                                            ×奥行
                                                            {
                                                                product.grill_depth_mm
                                                            }
                                                            ×高さ
                                                            {
                                                                product.grill_height_mm
                                                            }
                                                        </dd>
                                                    </dl>
                                                    <dl>
                                                        <dt>電源</dt>
                                                        <dd>
                                                            {
                                                                product.power_source
                                                            }
                                                        </dd>
                                                    </dl>
                                                </motion.div>
                                                <div className="swiper_wrap">
                                                    {product.carousel_images
                                                        .length > 0 && (
                                                        <Swiper
                                                            modules={[
                                                                Pagination,
                                                                Navigation,
                                                            ]}
                                                            loop={true}
                                                            navigation={true}
                                                            pagination={{
                                                                clickable: true,
                                                            }}
                                                            slidesPerView={1}
                                                            spaceBetween={10}
                                                            // centeredSlides={true}
                                                            // autoplay={{
                                                            //     delay: 3000,
                                                            //     disableOnInteraction: false,
                                                            // }}
                                                        >
                                                            {product.carousel_images
                                                                .sort(
                                                                    (a, b) =>
                                                                        a.sort_order -
                                                                        b.sort_order
                                                                )
                                                                .map(
                                                                    (image) => (
                                                                        <SwiperSlide
                                                                            key={
                                                                                image.id
                                                                            }
                                                                        >
                                                                            <img
                                                                                src={normalizeImagePath(image.image_url)}
                                                                                alt={`${product.name} ${product.model}`}
                                                                            />
                                                                        </SwiperSlide>
                                                                    )
                                                                )}
                                                        </Swiper>
                                                    )}
                                                </div>
                                                <div className="btn">
                                                    {product.inventory > 0 ? (
                                                        <Link
                                                            to={`/order/${product.id}`}
                                                            className="orange_btn cart btn noto-sans-jp-black"
                                                            onClick={() => {
                                                                const html =
                                                                    document.documentElement;
                                                                html.style.scrollBehavior =
                                                                    "auto";
                                                                setTimeout(
                                                                    () => {
                                                                        html.style.scrollBehavior =
                                                                            "smooth";
                                                                    },
                                                                    800
                                                                );
                                                            }}
                                                        >
                                                            {product.is_low_stock && (
                                                                <img
                                                                    className="low_stock_icon"
                                                                    src="/assets/img/common/low_stock.png"
                                                                    alt="残りわずか"
                                                                />
                                                            )}
                                                            注文画面へ
                                                        </Link>
                                                    ) : (
                                                        <div className="orange_btn cart btn noto-sans-jp-black">
                                                            注文画面へ
                                                            <div className="out_of_stock">
                                                                完売しました
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </section>
                        <section className="flowSec" id="flow">
                            <h2 className="fade-in-up">
                                ご利用の流れ<span>FLOW</span>
                            </h2>
                            <dl
                                className={`accDl fade-in-up`}
                                onClick={() =>
                                    setAccActive([
                                        accActive[0],
                                        !accActive[1],
                                        accActive[2],
                                    ])
                                }
                            >
                                <dt className={`${accActive[1] ? "open" : ""}`}>
                                    <h3>ご注文手順</h3>
                                    <p className="arrow"></p>
                                </dt>
                                <motion.div
                                    className="flow1 fade-in-up"
                                    initial={false}
                                    animate={{
                                        height: accActive[1] ? "auto" : 0,
                                    }}
                                    transition={{
                                        duration: 0.35,
                                        ease: [0.25, 0.46, 0.45, 0.94],
                                    }}
                                    style={{
                                        overflow: "hidden",
                                    }}
                                >
                                    <motion.dd
                                        initial={false}
                                        animate={{
                                            opacity: accActive[1] ? 1 : 0,
                                            y: accActive[1] ? 0 : -10,
                                        }}
                                        transition={{
                                            duration: 0.25,
                                            delay: accActive[1] ? 0.1 : 0,
                                            ease: "easeOut",
                                        }}
                                    >
                                        <ol className="exList">
                                            <li>
                                                <h4>
                                                    <span className="num">
                                                        1
                                                    </span>
                                                    ご購入商品の選択
                                                </h4>
                                                <p>
                                                    購入したい商品の
                                                    「注文画面へ」ボタンをクリックします。ご注文情報入力画面へ遷移します。
                                                </p>
                                            </li>
                                            <li>
                                                <h4>
                                                    <span className="num">
                                                        2
                                                    </span>
                                                    ご注文情報の入力・確認
                                                </h4>
                                                <p>
                                                    お名前やお届け先等、必要な情報を入力ください。最後に入力内容と商品名、数量、金額をご確認いただき間違いなければ「注文を確定する」ボタンをクリックします。
                                                    <br />
                                                    ※ご購入には携帯番号のご入力が必須です。
                                                </p>
                                            </li>
                                            <li>
                                                <h4>
                                                    <span className="num">
                                                        3
                                                    </span>
                                                    お支払情報の入力・確認
                                                </h4>
                                                <p>
                                                    お支払情報を入力ください。
                                                    <br />
                                                    最後に入力内容を確認いただき問題なければ支払完了ボタンをクリックします。
                                                </p>
                                            </li>
                                            <li>
                                                <h4>
                                                    <span className="num">
                                                        4
                                                    </span>
                                                    ご注文完了
                                                </h4>
                                                <p>
                                                    「ご注文いただきありがとうございました」という画面が表示されましたらご購入完了です。ご入力いただいた携帯電話番号に購入完了通知（SMS）をお送りしますので、ご確認ください。
                                                </p>
                                            </li>
                                        </ol>
                                    </motion.dd>
                                </motion.div>
                            </dl>
                            <dl
                                className={`accDl flow2 fade-in-up`}
                                onClick={() =>
                                    setAccActive([
                                        accActive[0],
                                        accActive[1],
                                        !accActive[2],
                                    ])
                                }
                            >
                                <dt
                                    className={`${accActive[2] ? "open" : ""} `}
                                >
                                    <h3>商品のお届け</h3>
                                    <p className="arrow"></p>
                                </dt>
                                <motion.div
                                    className="flow1 fade-in-up"
                                    initial={false}
                                    animate={{
                                        height: accActive[2] ? "auto" : 0,
                                    }}
                                    transition={{
                                        duration: 0.35,
                                        ease: [0.25, 0.46, 0.45, 0.94],
                                    }}
                                    style={{
                                        overflow: "hidden",
                                    }}
                                >
                                    <motion.dd
                                        initial={false}
                                        animate={{
                                            opacity: accActive[2] ? 1 : 0,
                                            y: accActive[2] ? 0 : -10,
                                        }}
                                        transition={{
                                            duration: 0.25,
                                            delay: accActive[2] ? 0.1 : 0,
                                            ease: "easeOut",
                                        }}
                                    >
                                        <ol className="exList arrowList">
                                            <li>
                                                <h4>
                                                    <span className="num">
                                                        1
                                                    </span>
                                                    もよりの西部ガス販売店
                                                    <br />
                                                    (リビングメイト)よりご連絡
                                                </h4>
                                                <p>
                                                    もよりの西部ガス販売店
                                                    (リビングメイト)
                                                    より、お支払い手続き完了後、お客さまへ日祝日を除く3日以内にお電話でご連絡いたします。
                                                </p>
                                                <ul className="attListMini">
                                                    <li>
                                                        ご連絡の際は、ご注文内容、お届け先、お届け日、お客さま情報の確認をさせていただきます。
                                                    </li>
                                                </ul>
                                            </li>
                                            <li>
                                                <h4>
                                                    <span className="num">
                                                        2
                                                    </span>
                                                    お届け・設置
                                                </h4>
                                                <p>
                                                    確認させていただいたご指定日にお届けいたします。
                                                </p>
                                            </li>
                                        </ol>
                                    </motion.dd>
                                </motion.div>
                            </dl>
                        </section>
                        <section className="areaSec" id="area">
                            <h2 className="fade-in-up">
                                配送対応エリア<span>AREA</span>
                            </h2>
                            <p className="fade-in-up">
                                西部ガス・西部ガス熊本・西部ガス佐世保・西部ガス長崎のサービスエリア内*が配送エリアとなります。
                            </p>
                            <ul className="attListMini fade-in-up">
                                <li>
                                    福岡・熊本・長崎の3県15市15町。詳しくは
                                    <a
                                        href="https://hd.saibugas.co.jp/business/gas/#serviceArea"
                                        target="_blank"
                                    >
                                        こちら
                                    </a>
                                    をご確認ください。
                                </li>
                            </ul>
                        </section>
                        <section className="paymentSec" id="payment">
                            <h2 className="fade-in-up">
                                お支払い方法<span>PAYMENT</span>
                            </h2>
                            <div className="accDl payment1 fade-in-up">
                                <dd>
                                    <div className="paymentWeb">
                                        <p>
                                            クレジットカード払いまたはPayPayがご利用いただけます。
                                        </p>
                                        <div className="whiteBox">
                                            <h3>
                                                ご利用可能なクレジットブランド
                                            </h3>
                                            <ul>
                                                <li>・MasterCard</li>
                                                <li>・Diners Club</li>
                                                <li>・VISA</li>
                                                <li>・JCB</li>
                                                <li>・American Express</li>
                                                <li>・DISCOVER</li>
                                            </ul>
                                        </div>
                                    </div>
                                </dd>
                            </div>
                            <p className="attMini fade-in-up">
                                ※お支払い期限については
                                <a href={userGuide} target="_blank">
                                    こちら
                                </a>
                                をご覧ください
                            </p>
                        </section>
                        <section className="faqSec" id="faq">
                            <h2 className="fade-in-up">
                                よくあるご質問<span>FAQ</span>
                            </h2>
                            <ul className="faqList fade-in-up">
                                {faqList.map((faq, index) => (
                                    <li key={index}>
                                        <dl
                                            onClick={() => {
                                                const newState = [...faqActive];
                                                newState[index] =
                                                    !newState[index];
                                                setFaqActive(newState);
                                            }}
                                        >
                                            <dt
                                                className={`${
                                                    faqActive[index]
                                                        ? "open"
                                                        : ""
                                                }`}
                                            >
                                                {faq.q}
                                            </dt>
                                            <motion.div
                                                className="faq-answer-wrapper"
                                                initial={false}
                                                animate={{
                                                    height: faqActive[index]
                                                        ? "auto"
                                                        : 0,
                                                }}
                                                transition={{
                                                    duration: 0.35,
                                                    ease: [
                                                        0.25, 0.46, 0.45, 0.94,
                                                    ],
                                                }}
                                                style={{
                                                    overflow: "hidden",
                                                }}
                                            >
                                                <motion.dd
                                                    className="flow1"
                                                    initial={false}
                                                    animate={{
                                                        opacity: faqActive[
                                                            index
                                                        ]
                                                            ? 1
                                                            : 0,
                                                        y: faqActive[index]
                                                            ? 0
                                                            : -10,
                                                    }}
                                                    transition={{
                                                        duration: 0.25,
                                                        delay: faqActive[index]
                                                            ? 0.1
                                                            : 0,
                                                        ease: "easeOut",
                                                    }}
                                                >
                                                    {faq.a}
                                                </motion.dd>
                                            </motion.div>
                                        </dl>
                                    </li>
                                ))}
                            </ul>
                        </section>

                        <section className="contactSec" id="contact">
                            <div className="cont_inr fade-in-up">
                                <img
                                    src="/assets/img/common/mail.svg"
                                    alt="メールでのお問い合わせ"
                                    className="mail_icon"
                                />
                                <h2>メールでのお問い合わせ</h2>
                                <p className="bHour">
                                    （受付時間：24時間365日受付）
                                </p>
                                <MailButton className="orange_btn arrow fade-in-up noto-sans-jp-black">
                                    お問い合わせ
                                </MailButton>
                                <p className="mail_annotation fade-in-up">
                                    ※件名は必ず変更せずにご送信ください。
                                    <br />
                                    ※お問い合わせ後5営業日以内を目処にご回答いたします。
                                    <br />
                                    ※メーラーが自動で起動しない場合は、お手数ですが「ecshop-info@saibugas-group.com」宛にお問い合わせをお願いいたします 。
                                </p>
                            </div>
                        </section>
                        <Footer />
                    </div>
                </main>
            </div>
        </>
    );
}
